import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, Grid, CircularProgress, List, ListItem, ListItemText, capitalize } from '@mui/material';
import { redirectPage, pagesUrl } from 'utils/misc';
import { createAndUpdate, createCoutesie, resendCode, doubleAuthenticationCodeValidation } from 'services/ApiGeneralService';
import dayjs from "dayjs";


const TwoFactorAuthModal = ({
  open,
  onClose,
  setOpenDialog,
  setRefreshFrom,
  setShowForm,
  setLoading,
  handlerBodyPersonNatural,
  returnParam = 0,
  brand,
  setValues,
  isCourtesie,
  venue,
  userFound,
  source,
  setFetchErrors,
  setCodeDoubleValidation,
  loading,
  timeLeft,
  setTimeLeft,
  showForm
}) => {
  const MAX_AGE = 13;
  const PERSON_NATURAL = "Persona Natural";
  const maxDate = dayjs(Date.now()).subtract(MAX_AGE, "years");
  const stateDefault = {
    person_type: PERSON_NATURAL,
    birth_date: maxDate,
    venue_id: venue,
    source_id: source,
    brand_id: brand,
    terms_condition: false,
    training_goals: [],
    related_information: true,
    contact_email: true,
    contact_sms: true,
    contact_app: true,
    contact_whatsapp: true,
    contact_phone: true,
  };
  const [code, setCode] = useState(Array(6).fill(""));
  const [canResend, setCanResend] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(null);
  const [messageError, setMessageError] = useState(null);

  const handleChange = (event, index) => {
    const newCode = [...code];
    newCode[index] = event.target.value;
    setCode(newCode);

    if (event.target.value && index < code.length - 1) {
      document.getElementById(`code-${index + 1}`).focus();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const fullCode = code.join("");
    let body = handlerBodyPersonNatural(fullCode);
  
    try {
      const validationResponse = await doubleAuthenticationCodeValidation(body);
      if (validationResponse.data.status === "error") {
        setMessageError(validationResponse?.data?.message[0]?.message);
        return;
      }

      if (validationResponse.data.status === "success") {
        setMessageSuccess(validationResponse?.data?.message);
        setMessageError(null);

        if (showForm === "prospect") {
          body.validate_exists = true;
        }
      }
  
      const updateResponse = await createAndUpdate(body);
      if (updateResponse.data.status === "error") {
        throw updateResponse.data.message;
      }
  
      if (updateResponse.data.status === "success") {
        handleCreateCourtesie(updateResponse.data.data);
        setOpenDialog(true);
        setRefreshFrom(true);
        onClose();
        setTimeout(() => {
          if (returnParam !== "1") {
            redirectPage(pagesUrl[brand]);
          } else {
            setShowForm("register");
            setRefreshFrom(false);
            setValues(stateDefault);
          }
        }, 1500);
      }
    } catch (error) {
      setFetchErrors(error);
      handlerBodyPersonNatural(null);
      setCodeDoubleValidation(null);
      onClose();
      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };  

  // Función para manejar el reenvío del código
  const handleResend = () => {
    setLoading(true);
    let body = handlerBodyPersonNatural(null)
    resendCode(body)
      .then((json) => {
        if (json.data.status === "error") {
          throw json.data.message;
        }
        if (json.data.status === "success" && json.data.message === "nuevo codigo enviado") {
          setTimeLeft(300);
          setCanResend(false);
          setCode(Array(6).fill(""));
          setCodeDoubleValidation(json.data.data.otp)
          setFetchErrors([]);
          setMessageSuccess(null);
          setMessageError(null);
        }
      })
      .catch((error) => {
        setFetchErrors(error);
        handlerBodyPersonNatural(null)
        setCodeDoubleValidation(null)
      })
      .finally(() => setLoading(false));;

  };

  // temporizador
  useEffect(() => {
    if (timeLeft === 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Función para formatear el tiempo restante en minutos:segundos
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secondsRemaining = seconds % 60;
    return `${minutes}:${secondsRemaining < 10 ? "0" : ""}${secondsRemaining}`;
  };

  const handleCreateCourtesie = (data) => {
    if (isCourtesie && (data?.id || data?.identity_user)) {
      const body = {
        "crm_leads_id": data?.id ?? Object.values(data?.identity_user)?.at(0),
        "user_creator": 2,
        "company_id": 1,
        "organization_id": 1,
        "brand_id": brand,
        "product_id": 10, //id de producto para que solo de una cortesia
        "document_number": data?.did || data?.document_number,
        "document_type": data?.document_type || data?.document_type_id,
        "daysToGive": 0,
        "date_start": "2024-06-19 00:00:00",
        "authorization_type": 3, // cortesia evento chico
        "venue_id": venue
      }

      createCoutesie(body, userFound?.type_afiliate ?? 'lead')
        .then(() => {
        })
        .catch(console.error)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="two-factor-authentication"
      aria-describedby="enter-confirmation-code"
    >
      <Box sx={{ width: 400, p: 4, bgcolor: 'background.paper', mx: 'auto', mt: '10%', borderRadius: 2 }}>
        <Typography variant="h6" color="primary">
          Código de Doble Autenticación
        </Typography>
        <Typography variant="body2" align="center" sx={{ mt: 2, mb: 3 }}>
          Ingrese el código de autenticación que te llegó a tu correo
        </Typography>
        <Grid container spacing={1} justifyContent="center">
          {code.map((digit, index) => (
            <Grid item key={index}>
              <TextField
                id={`code-${index}`}
                value={digit}
                onChange={(event) => handleChange(event, index)}
                variant="outlined"
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: 'center', width: '40px' }
                }}
              />
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          Tiempo restante: {formatTime(timeLeft)}
        </Typography>
        {messageSuccess && (
          <Box
            sx={{
              mt: 2,
              bgcolor: '#28a745',
              color: 'white',
              borderRadius: 2,
              p: 1,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2">
              {messageSuccess}
            </Typography>
          </Box>
        )}
        {messageError && (
          <Box
            sx={{
              mt: 2,
              bgcolor: '#dc3545',
              color: 'white',
              borderRadius: 2,
              p: 1,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2">
              {messageError}
            </Typography>
          </Box>
        )}
        {canResend ? (
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            sx={{ mt: 3 }}
            onClick={handleResend}
          >
            Reenviar Código
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={handleSubmit}
            disabled={code.includes("") || messageSuccess}
          >
            {
              loading ?
                <CircularProgress color="inherit" />
                :
                <>
                  {"Enviar"}
                </>
            }
          </Button>
        )}
      </Box>
    </Modal>
  );
};

export default TwoFactorAuthModal;
